import { Create, useForm, useSelect } from "@refinedev/antd";
import {
  HttpError,
  IResourceComponentsProps,
  useApiUrl,
  useCustomMutation,
  useNavigation,
  useTranslate,
  useWarnAboutChange,
} from "@refinedev/core";
import { Col, Form, Input, Row, Select, Tabs } from "antd";
import dayjs from "dayjs";
import React, { useCallback, useMemo, useState } from "react";

import { DatePicker } from "components/datepicker";
import { API_RESOURCES, DEFAULT_INPUT_MAX_LENGTH, DEFAULT_TEXT_AREA_MAX_LENGTH } from "config";
import { INormalClass, IUser } from "interfaces";
import { formatUser } from "utils/modelHelper";
import { fuzzySearch } from "utils/searchHelper";
import { toLowerCaseNonAccentVietnamese } from "utils/vietnameseHelper";
import { StudentTable } from "./private/studentTable";
export const customFormat = (value: any) => ` ${dayjs(value).get("year")} - ${dayjs(value).add(1, "year").get("year")}`;

export const NormalClassCreate: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();

  const {
    formProps: { onFinish: _, form: formCreateNormalClass, ...restFormProps },
    saveButtonProps,
  } = useForm<INormalClass, HttpError>({
    errorNotification(error) {
      return {
        type: "error",
        message: error?.message ?? t("normal_class.notifications.createError"),
      };
    },
    successNotification: {
      type: "success",
      message: t("normal_class.notifications.createSuccess"),
    },
  });

  const { list } = useNavigation();
  const apiUrl = useApiUrl();
  const { setWarnWhen } = useWarnAboutChange();
  const [studentList, setStudentList] = useState<IUser[]>([]);

  const { mutate: createNormalClass } = useCustomMutation<INormalClass>();

  const handleCreateNormalClass = async (values: any) => {
    createNormalClass(
      {
        url: `${apiUrl}/normal_class/create`,
        method: "post",
        values: {
          ...values,
          teachers: values.teacherId ? values.teacherId : [],
          teacherId: undefined,
          students: studentList?.map((student) => student.id) ?? [],
          description: values?.description ?? "",
          schoolYear: values?.schoolYear ? String(dayjs(values.schoolYear).get("year")) : null,
        },
        errorNotification(error) {
          return {
            type: "error",
            message: error?.message ?? t("normal_class.notifications.createError"),
          };
        },
        successNotification: {
          type: "success",
          message: t("normal_class.notifications.createSuccess"),
        },
      },
      {
        onSuccess() {
          setWarnWhen?.(false);
          setTimeout(() => {
            list(API_RESOURCES.normalClasses);
          }, 100);
        },
      }
    );
  };

  const {
    selectProps: { showSearch: showSearchTeacher, onSearch: onSearchTeacher, ...teacherSelectProps },
    queryResult: teacherQueryResult,
  } = useSelect<IUser, HttpError>({
    resource: `${API_RESOURCES.users}/teachers`,
    optionLabel: "fullName",
    pagination: false,
  });

  const teacherSelectOptions = useMemo(
    () =>
      teacherQueryResult?.data?.data
        .sort((a, b) => (a.fullName ?? "").localeCompare(b.fullName ?? ""))
        .map((teacher) => ({
          value: teacher?.id,
          label: formatUser(teacher),
        })),
    [teacherQueryResult?.data?.data]
  );

  const updateStudentList = useCallback(
    (newStudentList: IUser[]) => {
      setStudentList(newStudentList);
      formCreateNormalClass?.setFieldsValue({
        students: newStudentList.map((item) => item.id),
      });
    },
    [formCreateNormalClass]
  );
  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form
        {...restFormProps}
        form={formCreateNormalClass}
        layout="vertical"
        onFinish={(values) => handleCreateNormalClass(values)}
      >
        <Tabs
          defaultActiveKey="1"
          items={[
            {
              key: "1",
              label: t("normal_class.schedule.tab.1"),
              children: (
                <Row gutter={[16, 24]}>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item
                      label={t("normal_class.fields.schoolYear")}
                      name={["schoolYear"]}
                      rules={[
                        {
                          required: true,
                          message: t("normal_class.errors.schoolYearRequired"),
                        },
                      ]}
                    >
                      <DatePicker
                        format={customFormat}
                        placeholder={t("normal_class.placeholders.schoolYear")}
                        style={{ width: "100%" }}
                        picker="year"
                        disabledDate={(date) => date < dayjs(new Date()).startOf("years").startOf("days")}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={8}>
                    <Form.Item
                      label={t("normal_class.fields.code")}
                      name={["code"]}
                      rules={[
                        {
                          required: true,
                          message: t("normal_class.errors.codeRequired"),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t("normal_class.placeholders.code")}
                        maxLength={DEFAULT_INPUT_MAX_LENGTH}
                        showCount
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={8}>
                    <Form.Item
                      label={t("normal_class.fields.name")}
                      name={["name"]}
                      rules={[
                        {
                          required: true,
                          message: t("normal_class.errors.nameRequired"),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t("normal_class.placeholders.name")}
                        maxLength={DEFAULT_INPUT_MAX_LENGTH}
                        showCount
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={8}>
                    <Form.Item
                      label={t("normal_class.fields.teacher")}
                      name={["teacherId"]}
                      rules={[
                        {
                          required: true,
                          message: t("normal_class.errors.teacherRequired"),
                        },
                      ]}
                    >
                      <Select
                        allowClear
                        mode="multiple"
                        placeholder={t("normal_class.placeholders.teacher")}
                        {...teacherSelectProps}
                        options={teacherSelectOptions}
                        showSearch
                        filterOption={(input, option) =>
                          fuzzySearch(
                            toLowerCaseNonAccentVietnamese(String(option?.label ?? "")),
                            toLowerCaseNonAccentVietnamese(input)
                          )
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item label={t("normal_class.fields.description")} name={["description"]}>
                      <Input.TextArea
                        rows={4}
                        placeholder={t("normal_class.placeholders.description")}
                        maxLength={DEFAULT_TEXT_AREA_MAX_LENGTH}
                        showCount
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ),
            },
            {
              key: "2",
              label: t("normal_class.schedule.tab.2"),
              children: (
                <StudentTable
                  studentList={studentList}
                  onStudentListChanged={(newStudentList) => {
                    updateStudentList(newStudentList);
                  }}
                  hasAdd
                  hasDelete
                />
              ),
            },
          ]}
        />
      </Form>
    </Create>
  );
};
